import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  fetchBundlePointer,
  type BundlePointer,
  type ChannelName,
} from "@/lib/api";
import { signInUrl } from "@/lib/auth-url";
import { formatDate } from "@/lib/time";
import { ReleaseNotesBody } from "@/components/ReleaseNotesBody";

interface BundlesState {
  stable: BundlePointer | null;
  beta: BundlePointer | null;
  loaded: boolean;
}

export function Home() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [bundles, setBundles] = useState<BundlesState>({
    stable: null,
    beta: null,
    loaded: false,
  });

  useEffect(() => {
    if (status === "authenticated") {
      navigate("/setup", { replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [stable, beta] = await Promise.all([
        fetchBundlePointer("stable").catch(() => null),
        fetchBundlePointer("beta").catch(() => null),
      ]);
      if (!cancelled) {
        setBundles({ stable, beta, loaded: true });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-24">
        <div className="flex justify-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (status === "authenticated") {
    return null;
  }

  return (
    <div className="animate-fade-in">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8 flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-10 h-10 text-blue-500"
              >
                <rect
                  x="2"
                  y="3"
                  width="20"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M8 21h8M12 17v4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="7"
                  y="7"
                  width="10"
                  height="6"
                  rx="1"
                  fill="currentColor"
                  opacity="0.3"
                />
              </svg>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
            Digital signage that runs{" "}
            <span className="text-blue-500">on your terms</span>
          </h1>

          <p className="text-lg sm:text-xl text-zinc-400 mb-4 leading-relaxed max-w-2xl mx-auto">
            Blazeboard is a local-first digital signage system for dispensaries.
            Your menus, your promotions, your data — all running on a Raspberry
            Pi in your store. No cloud dependency, no monthly bandwidth fees, no
            internet required at runtime.
          </p>

          <p className="text-base text-zinc-500 mb-10 max-w-xl mx-auto">
            Manage your devices, view release notes, and get personalized setup
            instructions for your installation.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={signInUrl(`${window.location.origin}/setup`)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-8 py-3.5 rounded-xl text-base transition-colors"
            >
              Sign in to get started
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </a>
            <a
              href="/releases"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-zinc-100 font-medium px-8 py-3.5 rounded-xl text-base transition-colors"
            >
              View releases
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="text-lg font-semibold text-zinc-200">
            Current release
          </h2>
          <Link
            to="/releases"
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Full changelog →
          </Link>
        </div>
        {!bundles.loaded ? (
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="skeleton h-48 rounded-xl lg:col-span-2" />
            <div className="skeleton h-48 rounded-xl" />
          </div>
        ) : bundles.stable || bundles.beta ? (
          <div className="grid gap-4 lg:grid-cols-3">
            {bundles.stable && (
              <BundleHero pointer={bundles.stable} tone="stable" />
            )}
            {bundles.beta && (
              <BundleHero
                pointer={bundles.beta}
                tone="beta"
                compact={!!bundles.stable}
              />
            )}
          </div>
        ) : (
          <div className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-xl p-6 text-center text-sm text-zinc-500">
            No bundles have been published yet.
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
              <svg
                className="w-5 h-5 text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-zinc-100 mb-2">
              Fully local, fully yours
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Everything runs on your Raspberry Pi. Your menu data, media
              assets, and display configurations never leave your network. No
              cloud accounts, no API keys, no data sharing.
            </p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-4">
              <svg
                className="w-5 h-5 text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062A1.125 1.125 0 013 16.81V8.688zM12.75 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062a1.125 1.125 0 01-1.683-.977V8.688z"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-zinc-100 mb-2">
              One-command install
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Flash a Pi, run one curl command, and your signage system is live.
              The orchestrator handles Docker, kiosk mode, health monitoring,
              disk management, and automatic updates.
            </p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4">
              <svg
                className="w-5 h-5 text-purple-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-zinc-100 mb-2">
              Automatic updates
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Your device checks for updates automatically and applies them with
              zero downtime. SHA-256 verified binaries, atomic installs, and
              automatic rollback if anything goes wrong.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

interface BundleHeroProps {
  pointer: BundlePointer;
  tone: ChannelName;
  compact?: boolean;
}

/**
 * The headline bundle card. Stable gets the double-wide hero treatment;
 * beta stays half-width and muted. When there's no stable bundle (only
 * beta, e.g. pre-launch) beta promotes to full width automatically.
 */
function BundleHero({ pointer, tone, compact }: BundleHeroProps) {
  const isStable = tone === "stable";
  const channelLabel = pointer.channel;
  const notes = pointer.customerReleaseNotes || pointer.releaseNotes;
  const targetHref = `/releases/${pointer.channel}/${pointer.version}`;

  return (
    <div
      className={`rounded-xl border p-6 transition-colors ${
        isStable
          ? "bg-gradient-to-br from-blue-500/10 via-zinc-900 to-zinc-900 border-blue-500/30 lg:col-span-2 hover:border-blue-500/40"
          : compact
            ? "bg-zinc-900 border-amber-500/20 hover:border-amber-500/40"
            : "bg-zinc-900 border-amber-500/30 hover:border-amber-500/40 lg:col-span-2"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
              isStable
                ? "bg-blue-500/15 text-blue-300 border border-blue-500/30"
                : "bg-amber-500/10 text-amber-300 border border-amber-500/30"
            }`}
          >
            {isStable ? "Latest stable" : "What's new in beta"}
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              isStable
                ? "bg-green-500/10 text-green-400 border border-green-500/20"
                : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
            }`}
          >
            {channelLabel}
          </span>
        </div>
        <span className="text-xs text-zinc-500">
          {formatDate(pointer.releasedAt)}
        </span>
      </div>

      <h3
        className={`font-mono font-bold text-zinc-100 mb-3 ${
          isStable ? "text-2xl sm:text-3xl" : "text-xl"
        }`}
      >
        v{pointer.version}
      </h3>

      {notes ? (
        <div className="mb-4">
          <ReleaseNotesBody notes={notes} />
        </div>
      ) : (
        <p className="text-sm italic text-zinc-500 mb-4">
          No release notes provided.
        </p>
      )}

      {pointer.componentVersions && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {Object.entries(pointer.componentVersions).map(([name, ver]) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono bg-zinc-800/70 text-zinc-400 border border-zinc-800"
            >
              <span className="text-zinc-500">{name}</span>
              <span className="text-zinc-300">{ver}</span>
            </span>
          ))}
        </div>
      )}

      <Link
        to={targetHref}
        className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${
          isStable
            ? "text-blue-300 hover:text-blue-200"
            : "text-amber-300 hover:text-amber-200"
        }`}
      >
        Release details
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </Link>
    </div>
  );
}
